/**
 * Industry Profile Utility
 *
 * Maps SIC codes (and business description keywords) to industry profiles
 * used across AgentPhone delegates for industry-appropriate terminology,
 * follow-up questions, and caller/visitor language.
 *
 * SIC Code Ranges Reference:
 *   01–09   Agriculture
 *   10–14   Mining
 *   15–17   Construction
 *   20–39   Manufacturing
 *   40–49   Transportation / Utilities
 *   50–51   Wholesale Trade
 *   52–59   Retail Trade
 *   60–67   Finance / Insurance / Real Estate
 *   70–89   Services
 *     70–79  Business / Personal Services (Hotels, Repair, etc.)
 *     80     Health Services (Dental: 8021, Medical: 8011, etc.)
 *     81     Legal Services
 *     82     Educational Services
 *     83     Social Services
 *     84–86  Museums, Membership Organizations
 *     87     Engineering / Management / Consulting
 *     88     Private Households
 *   91–99   Public Administration
 */

export type IndustryVertical =
  | "healthcare"      // SIC 80xx — medical, dental, therapy, chiropractic, optometry
  | "legal"           // SIC 81xx — law firms, attorneys
  | "financial"       // SIC 60–67 — banking, insurance, financial planning
  | "real_estate"     // SIC 65xx — residential/commercial real estate, property mgmt
  | "education"       // SIC 82xx — schools, tutoring, training
  | "home_services"   // SIC 15–17, 73xx — contractors, plumbing, HVAC, landscaping
  | "retail"          // SIC 52–59 — stores, e-commerce, product sales
  | "hospitality"     // SIC 58, 70xx — restaurants, hotels, event venues
  | "fitness"         // SIC 7991, 7999 — gyms, studios, personal training
  | "automotive"      // SIC 55, 75xx — car dealers, auto repair
  | "manufacturing"   // SIC 20–39 — industrial, electrical equipment, fabrication
  | "consulting"      // SIC 87xx — business consulting, IT, marketing agencies
  | "nonprofit"       // SIC 83–86 — nonprofits, charities, associations
  | "general";        // Default — generic business

export interface IndustryProfile {
  vertical: IndustryVertical;
  callerTerm: string;           // e.g. "patient", "client", "customer"
  appointmentTerm: string;      // e.g. "appointment", "consultation", "showing"
  serviceTerm: string;          // e.g. "treatment", "service", "session"
  providerTerm: string;         // e.g. "doctor", "attorney", "agent"

  /** Follow-up questions for scheduling/appointment intents */
  schedulingFollowUps: string[];

  /** Follow-up questions for reschedule/cancel intents */
  rescheduleFollowUps: string[];

  /** Follow-up questions for billing/account intents */
  billingFollowUps: string[];

  /** Follow-up questions for staff routing intents */
  staffRoutingFollowUps: string[];

  /** Follow-up questions for urgent/emergency intents */
  urgencyFollowUps: string[];

  /**
   * Comma-separated urgent/emergency signals for this industry.
   * Used in orchestrator routing rules for immediate-route guidance.
   */
  urgencySignals: string;

  /**
   * Example routing scenarios used in orchestrator prompt to teach routing format.
   * Three scenarios: ambiguous (ask new/existing), clear-existing (skip), urgent (route immediately).
   */
  routingExamples: {
    callerName: string;           // Generic example caller name
    ambiguousIntent: string;      // What the caller says (ambiguous)
    ambiguousQuestion: string;    // Follow-up question to ask
    ambiguousMarkerIntent: string;
    clearIntent: string;          // What the caller says (clearly existing)
    clearResponse: string;        // Agent response
    clearMarkerIntent: string;
    urgentIntent: string;         // What the caller says (urgent/emergency)
    urgentResponse: string;       // Agent response
    urgentMarkerIntent: string;
  };
}

// ── Industry Profile Definitions ──────────────────────────────────────────────

const PROFILES: Record<IndustryVertical, IndustryProfile> = {
  healthcare: {
    vertical: "healthcare",
    callerTerm: "patient",
    appointmentTerm: "appointment",
    serviceTerm: "treatment",
    providerTerm: "doctor",
    schedulingFollowUps: [
      "Are you a new or existing patient with us?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date or type of appointment you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a recent visit, a specific procedure, or an insurance question?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific doctor, department, or team member you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you describe your symptoms in one sentence so I can flag this as urgent for the team?",
    ],
    urgencySignals: "severe pain, injury, accident, fall, unable to move, swelling, bleeding, medical emergency",
    routingExamples: {
      callerName: "Alex Smith",
      ambiguousIntent: "I need to schedule an appointment",
      ambiguousQuestion: "Are you a new or existing patient with us?",
      ambiguousMarkerIntent: "schedule an appointment",
      clearIntent: "I need to reschedule my appointment",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "reschedule my appointment",
      urgentIntent: "I'm in a lot of pain and need to be seen today",
      urgentResponse: "I'm so sorry to hear that — let me get our team on this right away.",
      urgentMarkerIntent: "severe pain, needs urgent appointment today",
    },
  },

  legal: {
    vertical: "legal",
    callerTerm: "client",
    appointmentTerm: "consultation",
    serviceTerm: "legal service",
    providerTerm: "attorney",
    schedulingFollowUps: [
      "Is this for a new matter or an existing case?",
      "And is there a preferred day or time for the consultation?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the consultation you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a recent invoice, retainer, or a specific billing question?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific attorney or paralegal you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the legal matter so I can flag it as urgent for the team?",
    ],
    urgencySignals: "arrested, served papers, court deadline, emergency hearing, urgent legal matter",
    routingExamples: {
      callerName: "Morgan Lee",
      ambiguousIntent: "I need to speak with an attorney",
      ambiguousQuestion: "Is this for a new matter or an existing case?",
      ambiguousMarkerIntent: "speak with an attorney",
      clearIntent: "I need to reschedule my consultation next week",
      clearResponse: "Of course — I'll make sure that gets taken care of right away.",
      clearMarkerIntent: "reschedule consultation",
      urgentIntent: "I just got served papers and need to speak with someone today",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — just served legal papers, needs immediate consultation",
    },
  },

  financial: {
    vertical: "financial",
    callerTerm: "client",
    appointmentTerm: "meeting",
    serviceTerm: "service",
    providerTerm: "advisor",
    schedulingFollowUps: [
      "Is this for a new client review or an existing account question?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the meeting you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a specific account, statement, or transaction?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific advisor or department you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "fraud, unauthorized transaction, account locked, identity theft, urgent account issue",
    routingExamples: {
      callerName: "Jordan Kim",
      ambiguousIntent: "I'd like to schedule a meeting",
      ambiguousQuestion: "Is this for a new client review or an existing account question?",
      ambiguousMarkerIntent: "schedule a meeting",
      clearIntent: "I need to cancel my appointment tomorrow",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "cancel appointment tomorrow",
      urgentIntent: "I think there's fraudulent activity on my account",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — suspected fraud on account",
    },
  },

  real_estate: {
    vertical: "real_estate",
    callerTerm: "client",
    appointmentTerm: "showing",
    serviceTerm: "service",
    providerTerm: "agent",
    schedulingFollowUps: [
      "Are you looking to buy, sell, or rent?",
      "And is there a preferred day or time for a showing or consultation?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the showing or meeting you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a specific property, listing, or contract?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific agent you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "deal falling through, contract deadline, urgent offer, title issue, closing emergency",
    routingExamples: {
      callerName: "Sam Rivera",
      ambiguousIntent: "I'd like to look at some properties",
      ambiguousQuestion: "Are you looking to buy, sell, or rent?",
      ambiguousMarkerIntent: "interested in viewing properties",
      clearIntent: "I need to reschedule my showing on Friday",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "reschedule showing on Friday",
      urgentIntent: "My offer is expiring today and I need to reach my agent",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — offer expiring today, needs agent immediately",
    },
  },

  education: {
    vertical: "education",
    callerTerm: "student",
    appointmentTerm: "session",
    serviceTerm: "program",
    providerTerm: "instructor",
    schedulingFollowUps: [
      "Is this for a new enrollment or an existing student?",
      "And is there a preferred day or time that works best?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the session you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about tuition, a specific invoice, or a financial aid question?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific instructor, department, or advisor you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the situation so I can flag it as urgent for the team?",
    ],
    urgencySignals: "safety concern, urgent enrollment deadline, suspension, immediate academic issue",
    routingExamples: {
      callerName: "Taylor Brooks",
      ambiguousIntent: "I'd like to sign up for a program",
      ambiguousQuestion: "Is this for a new enrollment or an existing student?",
      ambiguousMarkerIntent: "interested in signing up for a program",
      clearIntent: "I need to cancel my session this week",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "cancel session this week",
      urgentIntent: "I need to reach someone urgently about my enrollment status",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — enrollment status issue needs immediate attention",
    },
  },

  home_services: {
    vertical: "home_services",
    callerTerm: "customer",
    appointmentTerm: "appointment",
    serviceTerm: "service",
    providerTerm: "technician",
    schedulingFollowUps: [
      "Is this for a new service request or a follow-up on existing work?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the appointment you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a recent job, an estimate, or a specific invoice?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific technician or department you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "flood, gas leak, no heat, electrical emergency, pipe burst, active leak, safety hazard",
    routingExamples: {
      callerName: "Casey Morgan",
      ambiguousIntent: "I need someone to come take a look at something",
      ambiguousQuestion: "Is this for a new service request or a follow-up on existing work?",
      ambiguousMarkerIntent: "service request or follow-up",
      clearIntent: "I need to reschedule my service appointment",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "reschedule service appointment",
      urgentIntent: "I have a pipe that just burst and water is everywhere",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "emergency — burst pipe, active flooding",
    },
  },

  retail: {
    vertical: "retail",
    callerTerm: "customer",
    appointmentTerm: "appointment",
    serviceTerm: "product",
    providerTerm: "associate",
    schedulingFollowUps: [
      "Is there a specific product, order, or service you have in mind?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Is this about an order, return, or delivery you'd like to change?",
    ],
    billingFollowUps: [
      "Is this about a recent order, payment, or refund request?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific department or team member you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "safety issue, product recall, damaged item, urgent delivery, order problem",
    routingExamples: {
      callerName: "Riley Adams",
      ambiguousIntent: "I have a question about an order",
      ambiguousQuestion: "Is this about a recent purchase or a new order you'd like to place?",
      ambiguousMarkerIntent: "order inquiry",
      clearIntent: "I need to return something I bought last week",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "return item from last week",
      urgentIntent: "I received a damaged product and need this resolved today",
      urgentResponse: "I'm sorry to hear that — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — damaged product received, needs immediate resolution",
    },
  },

  hospitality: {
    vertical: "hospitality",
    callerTerm: "guest",
    appointmentTerm: "reservation",
    serviceTerm: "service",
    providerTerm: "host",
    schedulingFollowUps: [
      "Is this for a new reservation or an existing booking?",
      "And is there a preferred date or time that works best?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the reservation you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a recent visit, a specific charge, or a gift card?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific department or team member you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "accident on premises, food safety concern, injury, urgent reservation issue",
    routingExamples: {
      callerName: "Jamie Patel",
      ambiguousIntent: "I'd like to make a reservation",
      ambiguousQuestion: "Is this for a new reservation or a change to an existing one?",
      ambiguousMarkerIntent: "reservation inquiry",
      clearIntent: "I need to cancel my reservation for this weekend",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "cancel reservation this weekend",
      urgentIntent: "There's a problem at my table and I need to speak with a manager now",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — guest issue requiring manager immediately",
    },
  },

  fitness: {
    vertical: "fitness",
    callerTerm: "member",
    appointmentTerm: "session",
    serviceTerm: "class",
    providerTerm: "trainer",
    schedulingFollowUps: [
      "Are you a current member or interested in joining?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the session you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about your membership, a specific charge, or a package question?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific trainer or class instructor you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "injury during workout, medical emergency, fallen, hurt, safety concern",
    routingExamples: {
      callerName: "Drew Collins",
      ambiguousIntent: "I'm interested in signing up",
      ambiguousQuestion: "Are you a current member or looking to join for the first time?",
      ambiguousMarkerIntent: "interested in membership",
      clearIntent: "I need to cancel my personal training session tomorrow",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "cancel personal training session tomorrow",
      urgentIntent: "Someone just got hurt in the gym and needs help",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "emergency — member injured in gym, needs immediate assistance",
    },
  },

  automotive: {
    vertical: "automotive",
    callerTerm: "customer",
    appointmentTerm: "appointment",
    serviceTerm: "service",
    providerTerm: "service advisor",
    schedulingFollowUps: [
      "Is this for a service appointment, a test drive, or a vehicle inquiry?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the appointment you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a recent service, an estimate, or a specific invoice?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific service advisor or sales associate you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "vehicle on fire, accident, safety recall, brake failure, urgent safety issue",
    routingExamples: {
      callerName: "Chris Navarro",
      ambiguousIntent: "I need to bring my car in",
      ambiguousQuestion: "Is this for a routine service or is there a specific issue you're experiencing?",
      ambiguousMarkerIntent: "vehicle service inquiry",
      clearIntent: "I need to reschedule my oil change appointment",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "reschedule oil change appointment",
      urgentIntent: "My brakes are making a bad noise and I'm worried it's not safe to drive",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — brake safety concern, may not be safe to drive",
    },
  },

  manufacturing: {
    vertical: "manufacturing",
    callerTerm: "customer",
    appointmentTerm: "appointment",
    serviceTerm: "product or service",
    providerTerm: "representative",
    schedulingFollowUps: [
      "Is this for a new order, installation, or a follow-up on existing work?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the appointment you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a quote, order, or a specific invoice?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific sales representative or engineer you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "equipment failure, power outage, safety hazard, urgent delivery, production downtime",
    routingExamples: {
      callerName: "Jordan Lee",
      ambiguousIntent: "I need help with a transfer switch order",
      ambiguousQuestion: "Is this for a new order or follow-up on existing work?",
      ambiguousMarkerIntent: "transfer switch inquiry",
      clearIntent: "I need to reschedule my site visit",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "reschedule site visit",
      urgentIntent: "Our equipment is down and we need someone today",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — equipment down, needs immediate support",
    },
  },

  consulting: {
    vertical: "consulting",
    callerTerm: "client",
    appointmentTerm: "meeting",
    serviceTerm: "service",
    providerTerm: "consultant",
    schedulingFollowUps: [
      "Is this for a new project inquiry or an existing engagement?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the meeting you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a recent invoice, proposal, or a specific project?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific consultant or team you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "system outage, data breach, urgent project deadline, critical issue, escalation needed",
    routingExamples: {
      callerName: "Quinn Foster",
      ambiguousIntent: "I'd like to talk to someone about your services",
      ambiguousQuestion: "Is this for a new project inquiry or an existing engagement?",
      ambiguousMarkerIntent: "interested in consulting services",
      clearIntent: "I need to move my meeting to later this week",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "reschedule meeting to later this week",
      urgentIntent: "We have a critical system issue and need someone on the phone immediately",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — critical system issue requiring immediate support",
    },
  },

  nonprofit: {
    vertical: "nonprofit",
    callerTerm: "caller",
    appointmentTerm: "appointment",
    serviceTerm: "program",
    providerTerm: "staff member",
    schedulingFollowUps: [
      "Is this about a program, event, or volunteer opportunity?",
      "And is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the appointment or event you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a donation, membership, or specific program question?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific program director or department you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the situation so I can flag this as urgent for the team?",
    ],
    urgencySignals: "safety concern, emergency assistance needed, urgent situation, immediate need",
    routingExamples: {
      callerName: "Avery Johnson",
      ambiguousIntent: "I'd like to get involved with your organization",
      ambiguousQuestion: "Are you interested in volunteering, donating, or one of our programs?",
      ambiguousMarkerIntent: "interested in getting involved",
      clearIntent: "I need to cancel my volunteer shift this Saturday",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "cancel volunteer shift this Saturday",
      urgentIntent: "I need emergency assistance right away",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent — caller needs emergency assistance immediately",
    },
  },

  general: {
    vertical: "general",
    callerTerm: "customer",
    appointmentTerm: "appointment",
    serviceTerm: "service",
    providerTerm: "team member",
    schedulingFollowUps: [
      "Is there a preferred day or time that works best for you?",
    ],
    rescheduleFollowUps: [
      "Do you know the date of the appointment you'd like to reschedule or cancel?",
    ],
    billingFollowUps: [
      "Is this about a recent transaction or a specific account question?",
    ],
    staffRoutingFollowUps: [
      "Is there a specific person or department you'd like to reach?",
    ],
    urgencyFollowUps: [
      "Can you briefly describe the issue so I can flag this as urgent for the team?",
    ],
    urgencySignals: "urgent issue, safety concern, emergency, needs immediate attention",
    routingExamples: {
      callerName: "Alex Smith",
      ambiguousIntent: "I need to schedule an appointment",
      ambiguousQuestion: "Is there a preferred day or time that works best for you?",
      ambiguousMarkerIntent: "schedule an appointment",
      clearIntent: "I need to reschedule my appointment",
      clearResponse: "Of course — I'll take care of that for you right away.",
      clearMarkerIntent: "reschedule appointment",
      urgentIntent: "I have an urgent issue that needs to be dealt with right away",
      urgentResponse: "I understand — let me get our team on this right away.",
      urgentMarkerIntent: "urgent issue requiring immediate attention",
    },
  },
};

// ── SIC Code → Vertical Mapping ───────────────────────────────────────────────

function isHealthcareSicCode(sicCode: string): boolean {
  // Direct-care healthcare only — not all SIC 80xx (labs, admin, etc.)
  return (
    sicCode.startsWith("8011") ||
    sicCode.startsWith("8021") ||
    sicCode.startsWith("8031") ||
    sicCode.startsWith("8041") ||
    sicCode.startsWith("8042") ||
    sicCode.startsWith("8043") ||
    sicCode.startsWith("8049")
  );
}

function sicCodeToVertical(sicCode: string | null | undefined): IndustryVertical {
  if (!sicCode) return "general";
  const code = sicCode.trim();
  const prefix2 = code.slice(0, 2);
  const prefix4 = code.slice(0, 4);
  const prefixNum = Number.parseInt(prefix2, 10);

  // Health Services (SIC 80xx) — patient terminology only for direct-care codes
  if (prefix2 === "80") {
    return isHealthcareSicCode(code) ? "healthcare" : "consulting";
  }

  // Legal Services (SIC 81xx)
  if (prefix2 === "81") return "legal";

  // Educational Services (SIC 82xx)
  if (prefix2 === "82") return "education";

  // Social / Nonprofit Services (SIC 83–86)
  if (["83", "84", "85", "86"].includes(prefix2)) return "nonprofit";

  // Engineering / Management / Consulting (SIC 87xx)
  if (prefix2 === "87") return "consulting";

  // Finance / Banking (SIC 60–62)
  if (["60", "61", "62"].includes(prefix2)) return "financial";

  // Insurance (SIC 63–64)
  if (["63", "64"].includes(prefix2)) return "financial";

  // Real Estate (SIC 65xx)
  if (prefix2 === "65") return "real_estate";

  // Automotive dealers / service (SIC 55, 75xx)
  if (prefix2 === "55" || prefix2 === "75") return "automotive";

  // Manufacturing (SIC 20–39) — industrial, electrical equipment, engineering products
  if (!Number.isNaN(prefixNum) && prefixNum >= 20 && prefixNum <= 39) {
    return "manufacturing";
  }

  // Wholesale Trade (SIC 50–51)
  if (prefix2 === "50" || prefix2 === "51") return "retail";

  // Retail (SIC 52–59)
  if (["52", "53", "54", "56", "57", "59"].includes(prefix2)) return "retail";

  // Eating / Drinking (SIC 58)
  if (prefix2 === "58") return "hospitality";

  // Hotels / Lodging (SIC 70)
  if (prefix2 === "70") return "hospitality";

  // Business / Repair Services (SIC 73, 76)
  if (prefix2 === "73" || prefix2 === "76") return "home_services";

  // Construction (SIC 15–17)
  if (["15", "16", "17"].includes(prefix2)) return "home_services";

  // Special trade contractors (home services sub-types)
  if (prefix2 === "17") return "home_services";

  // Fitness / Recreation (SIC 7991, 7999)
  if (prefix4 === "7991" || prefix4 === "7999") return "fitness";

  // Amusement / Recreation Services (SIC 79xx)
  if (prefix2 === "79") return "hospitality";

  return "general";
}

/**
 * Resolve the industry profile for a project.
 * Uses SIC code as primary signal, falls back to keyword scanning of
 * business description for projects without a SIC code.
 */
export function resolveIndustryProfile(
  sicCode: string | null | undefined,
  businessDescription: string | null | undefined,
): IndustryProfile {
  const vertical = sicCodeToVertical(sicCode);
  if (vertical !== "general") {
    return PROFILES[vertical]!;
  }

  const desc = (businessDescription ?? "").toLowerCase();

  // Manufacturing equipment makers before generic "engineering company" matches
  // (e.g. Electric Equipment & Engineering Company).
  if (
    desc.includes("manufacturing") ||
    desc.includes("fabrication") ||
    desc.includes("transfer switch") ||
    desc.includes("electrical equipment") ||
    desc.includes("electric equipment") ||
    desc.includes("industrial equipment") ||
    desc.includes("power distribution")
  ) {
    return PROFILES.manufacturing!;
  }

  // MEP / AEC engineering firms before home_services — "electrical"/"plumbing" alone
  // are service lines on engineering sites (e.g. BCER), not trade contractors.
  if (
    desc.includes("mep engineering") ||
    desc.includes("mep firm") ||
    desc.includes("engineering firm") ||
    desc.includes("engineering company") ||
    desc.includes("engineering services") ||
    desc.includes("life safety") ||
    (desc.includes("mechanical") && desc.includes("electrical") && desc.includes("plumbing")) ||
    (desc.includes("engineering") &&
      !desc.includes("software engineering") &&
      (desc.includes("technology") ||
        desc.includes("commissioning") ||
        desc.includes("fire protection") ||
        desc.includes("built environment")))
  ) {
    return PROFILES.consulting!;
  }

  if (
    desc.includes("plumber") ||
    desc.includes("plumbing company") ||
    desc.includes("plumbing service") ||
    desc.includes("plumbing repair") ||
    desc.includes("hvac") ||
    desc.includes("landscaping") ||
    desc.includes("contractor") ||
    desc.includes("roofing") ||
    desc.includes("electrician") ||
    desc.includes("electrical contractor") ||
    desc.includes("electrical repair") ||
    desc.includes("cleaning service") ||
    desc.includes("drain") ||
    desc.includes("sewer") ||
    desc.includes("water heater")
  ) {
    return PROFILES.home_services!;
  }

  if (desc.includes("engineering") && !desc.includes("software engineering")) {
    return PROFILES.consulting!;
  }

  // Churches / faith communities BEFORE education — parish sites often mention
  // "school", "campus ministry", or "admissions" for religious education.
  if (
    desc.includes("church") ||
    desc.includes("catholic") ||
    desc.includes("parish") ||
    desc.includes("diocese") ||
    desc.includes("congregation") ||
    desc.includes("ministry") ||
    desc.includes("worship") ||
    desc.includes("synagogue") ||
    desc.includes("mosque") ||
    desc.includes("temple") ||
    desc.includes("faith community") ||
    desc.includes("religious")
  ) {
    return PROFILES.nonprofit!;
  }

  if (
    desc.includes("nonprofit") ||
    desc.includes("non-profit") ||
    desc.includes("charity") ||
    desc.includes("foundation") ||
    desc.includes("association") ||
    desc.includes("food pantry") ||
    desc.includes("homeless shelter") ||
    desc.includes("community center")
  ) {
    return PROFILES.nonprofit!;
  }

  // Education BEFORE healthcare — university/college sites often mention nursing,
  // clinical programs, "patient care" coursework, etc. (e.g. WGU) without being
  // a healthcare practice. Prefer strong academic signals over lone "school".
  if (
    desc.includes("high school") ||
    desc.includes("middle school") ||
    desc.includes("elementary school") ||
    desc.includes("degree program") ||
    desc.includes("online university") ||
    desc.includes("online college") ||
    desc.includes("university") ||
    desc.includes("college") ||
    desc.includes("registrar") ||
    desc.includes("prospective student") ||
    desc.includes("tuition") ||
    desc.includes("financial aid") ||
    (desc.includes("admissions") &&
      (desc.includes("university") ||
        desc.includes("college") ||
        desc.includes("enrollment") ||
        desc.includes("degree"))) ||
    (desc.includes("enrollment") &&
      (desc.includes("university") || desc.includes("college") || desc.includes("student"))) ||
    (desc.includes("campus") &&
      (desc.includes("university") || desc.includes("college") || desc.includes("student"))) ||
    (desc.includes("academy") &&
      (desc.includes("student") || desc.includes("tuition") || desc.includes("admissions"))) ||
    (desc.includes("school") &&
      (desc.includes("high school") ||
        desc.includes("elementary") ||
        desc.includes("middle school") ||
        desc.includes("student") ||
        desc.includes("teacher") ||
        desc.includes("principal"))) ||
    desc.includes("tutoring") ||
    /\.edu\b/.test(desc)
  ) {
    return PROFILES.education!;
  }

  // Real estate BEFORE healthcare — title companies, "closing clinic", and
  // therapy-adjacent copy on listing pages must not beat "real estate" / cash-home.
  if (
    desc.includes("real estate") ||
    desc.includes("realtor") ||
    desc.includes("realty") ||
    desc.includes("property management") ||
    desc.includes("home buying") ||
    desc.includes("we buy houses") ||
    desc.includes("cash for homes") ||
    desc.includes("sell your home")
  ) {
    return PROFILES.real_estate!;
  }

  // Healthcare / clinical practices — prefer practice-oriented phrases.
  // Do not treat lone "clinical", "patient", "appointment", or "clinic" as sufficient.
  if (
    desc.includes("dental") ||
    desc.includes("dentist") ||
    desc.includes("orthodont") ||
    desc.includes("chiropractic") ||
    desc.includes("chiropractor") ||
    desc.includes("physical therapy") ||
    desc.includes("physiotherapy") ||
    desc.includes("healthcare") ||
    desc.includes("health care") ||
    desc.includes("medical practice") ||
    desc.includes("medical clinic") ||
    desc.includes("doctor") ||
    desc.includes("physician") ||
    desc.includes("optom") ||
    (/\bclinic\b/.test(desc) &&
      (desc.includes("medical") ||
        desc.includes("patient") ||
        desc.includes("therapy") ||
        desc.includes("health"))) ||
    (desc.includes("patient") &&
      (desc.includes("appointment") ||
        desc.includes("intake") ||
        desc.includes("provider") ||
        desc.includes("medical")))
  ) {
    return PROFILES.healthcare!;
  }

  if (desc.includes("attorney") || desc.includes("law firm") || desc.includes("legal") ||
      desc.includes("lawyer")) {
    return PROFILES.legal!;
  }

  // Schools before financial — "financial aid" must not classify high schools as financial.
  // (Education block above already covers the main school signals; keep a late fallback.)
  if (
    desc.includes("high school") ||
    desc.includes("middle school") ||
    desc.includes("elementary school") ||
    desc.includes("academy") ||
    desc.includes("admissions") ||
    desc.includes("enrollment") ||
    desc.includes("tuition") ||
    desc.includes("school") ||
    desc.includes("tutoring") ||
    desc.includes("university") ||
    desc.includes("college") ||
    desc.includes("campus") ||
    /\.edu\b/.test(desc)
  ) {
    return PROFILES.education!;
  }

  if (
    (desc.includes("financial") && !desc.includes("financial aid")) ||
    desc.includes("financial advisor") ||
    desc.includes("wealth management") ||
    desc.includes("insurance agency") ||
    desc.includes("insurance broker") ||
    desc.includes("bank") ||
    desc.includes("invest")
  ) {
    return PROFILES.financial!;
  }

  if (desc.includes("gym") || desc.includes("fitness") || desc.includes("yoga") ||
      desc.includes("pilates") || desc.includes("personal trainer")) {
    return PROFILES.fitness!;
  }

  if (desc.includes("restaurant") || desc.includes("hotel") || desc.includes("catering") ||
      desc.includes("event venue") || desc.includes("hospitality")) {
    return PROFILES.hospitality!;
  }

  if (desc.includes("automotive") || desc.includes("auto repair") || desc.includes("car dealer") ||
      desc.includes("dealership")) {
    return PROFILES.automotive!;
  }

  if (desc.includes("consulting") || desc.includes("agency") || desc.includes("marketing") ||
      desc.includes("it services") || desc.includes("technology services")) {
    return PROFILES.consulting!;
  }

  if (desc.includes("nonprofit") || desc.includes("non-profit") || desc.includes("charity") ||
      desc.includes("foundation") || desc.includes("association") ||
      desc.includes("church") || desc.includes("parish") || desc.includes("ministry")) {
    return PROFILES.nonprofit!;
  }

  return PROFILES.general!;
}

/** Industry-appropriate labels for emails, inbox UI, and AI prompts. */
export function getIndustryEmailTerms(
  sicCode: string | null | undefined,
  businessDescription: string | null | undefined,
): {
  term: string;
  termCap: string;
  providerTerm: string;
  providerTermCap: string;
  isHealthcare: boolean;
} {
  const profile = resolveIndustryProfile(sicCode, businessDescription);
  const term = profile.callerTerm || "customer";
  const providerTerm = profile.providerTerm || "team member";
  return {
    term,
    termCap: term.charAt(0).toUpperCase() + term.slice(1),
    providerTerm,
    providerTermCap: providerTerm.charAt(0).toUpperCase() + providerTerm.slice(1),
    isHealthcare: profile.vertical === "healthcare",
  };
}

/**
 * Short industry-specific example clause for SMS Team Handoff intake
 * (after name/email are confirmed). Avoids hardcoding real-estate
 * "property address / timeline" language for every vertical.
 */
export function smsHandoffDetailExample(profile: IndustryProfile): string {
  switch (profile.vertical) {
    case "real_estate":
      return "for example, the property address or your timeline";
    case "legal":
      return "for example, whether this is a new matter and what type of legal help you need";
    case "healthcare":
      return "for example, whether you're a new or existing patient and a preferred day or time";
    case "home_services":
      return "for example, the service address and what you need done";
    case "financial":
      return "for example, what kind of financial help you're looking for";
    case "automotive":
      return "for example, the vehicle and what service you need";
    case "education":
      return "for example, the program or student this is about";
    case "consulting":
      return "for example, the project or challenge you need help with";
    default:
      return "for example, a bit more detail about what you need";
  }
}

/** Full follow-up question after Text Me / signup details are already on file. */
export function smsTextMeDetailQuestion(profile: IndustryProfile): string {
  switch (profile.vertical) {
    case "real_estate":
      return "Can you tell me more about the property — location, condition, and your timeline?";
    case "legal":
      return "Can you tell me a bit more about what legal help you need so I can pass the right details to the team?";
    case "healthcare":
      return "Can you tell me a bit more about what you need — for example, a preferred day or time?";
    case "home_services":
      return "Can you tell me more about the job — address and what you need done?";
    default: {
      const example = smsHandoffDetailExample(profile);
      return `Can you tell me a bit more about what you need — ${example}?`;
    }
  }
}

/** After email is confirmed on SMS main orchestrator (Text Me). */
export function smsTextMeAfterEmailQuestion(
  profile: IndustryProfile,
  email: string,
  firstName = "there",
): string {
  if (profile.vertical === "real_estate") {
    return `Perfect, ${firstName}! I've got ${email} on file. Can you share a bit more about the property — address, condition, and when you'd like to sell?`;
  }
  const example = smsHandoffDetailExample(profile);
  return `Perfect, ${firstName}! I've got ${email} on file. Can you tell me a bit more about what you need — ${example}?`;
}
